/**
 * La lista de inscritos del torneo: filtrar, buscar, y ver quién pagó.
 *
 * ES LA PANTALLA DEL SÁBADO A LA MAÑANA. Quien organiza llega a la cancha con
 * el teléfono, abre esto y necesita responder tres cosas de un vistazo:
 * quiénes vienen, en qué categoría, y a quién hay que cobrarle. Los íconos
 * son para eso — a distancia de brazo y con sol encima, un ícono se lee y una
 * palabra en gris no.
 *
 * CUATRO SITUACIONES, CUATRO ÍCONOS, que es lo que pidió Javier:
 *  · foto        → mandó la imagen del SINPE o el depósito
 *  · comentario  → escribió que pagó («en efectivo el martes»)
 *  · reloj       → se anotó y no mandó nada: se le cae sola en un rato
 *  · mano        → lo metió la organización (a ese no se le cae nada)
 *
 * El reloj y la mano pueden salir juntos: alguien anotado por la organización
 * que igual no pagó. Son dos preguntas distintas —«¿pagó?» y «¿quién lo
 * anotó?»— y juntarlas en un solo ícono perdería una de las dos.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categoryKey, canPlayLevel } from '@tennisup/contracts';
import type { CommunityCategory, Tournament, TournamentEntry } from '@tennisup/contracts';
import {
  ENTRY_REVIEW_NOTE_MAX,
  checkPair,
  enterableCategories,
  partnerSexFor,
  playsIn,
} from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { formatColones } from './CategoryPicker';
import { ChoiceGroup } from './ChoiceGroup';
import {
  BankIcon,
  CheckIcon,
  ClockIcon,
  CommentIcon,
  CrossIcon,
  HandIcon,
  PhotoIcon,
  StarIcon,
} from './icons';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { Paginador, usePaginado } from './Paginado';
import { usePlayerSearch, MIN_SEARCH_LENGTH, claveDeBusqueda } from '../hooks/usePlayerSearch';
import { nombreParaLeer } from '../lib/nombreParaLeer';
import { categoryName } from '../lib/categoryName';

/**
 * El valor del filtro «todas».
 *
 * Una palabra y no la cadena vacía: es el valor de un radio y termina en un
 * `id` del HTML, y `id="entries-category-"` es un id a medias. `categoryKey`
 * arma «NIVEL-SEXO», así que nunca puede chocar con esto.
 */
const TODAS = 'TODAS';

/**
 * `0` si esta inscripción está esperando una decisión, `1` si no.
 *
 * Es la clave de orden que sube arriba lo que hay que revisar. Un número y no
 * un booleano porque va restado dentro de un `sort`, donde un `false - true`
 * funciona por accidente y se lee peor.
 *
 * PENDING Y NO «SIN COMPROBANTE»: lo que decide es el estado de la REVISIÓN.
 * Alguien que mandó la foto y ya fue aprobado no vuelve a la fila, y alguien
 * a quien se rechazó tampoco — esa decisión ya se tomó, y si hay que cambiarla
 * se busca a la persona por su nombre.
 */
function porRevisar(entry: TournamentEntry): number {
  return entry.reviewState === 'PENDING' ? 0 : 1;
}

/**
 * SI ESTA INSCRIPCIÓN QUEDÓ FUERA DEL NIVEL DE ESA PERSONA.
 *
 * Solo pasa en un torneo abierto: con el torneo cerrado la regla no deja
 * anotarse fuera de nivel, así que esto siempre da `false`. Javier lo pidió
 * junto con el check: «poner un cuadro rojo para que el admin sepa que tiene
 * un nivel superior o muy inferior a la inscripción».
 *
 * `null` NO ES «FUERA DE NIVEL». Quien no es socia del club no tiene
 * categoría asignada contra la cual comparar; que venga de otro club es
 * normal en un torneo abierto y se dice con otra etiqueta, gris, no en rojo.
 *
 * La regla la contesta `canPlayLevel` del contrato, la misma que usa el
 * servidor: si acá se volviera a escribir, un día una diría rojo y la otra
 * dejaría pasar.
 */
function fueraDeNivel(entry: TournamentEntry): boolean {
  return entry.assignedLevel !== null && !canPlayLevel(entry.assignedLevel, entry.level);
}

export function EntryRoster({
  tournament,
  viewerId,
  viewerSex,
  onChanged,
}: {
  tournament: Tournament;
  /** Quién está mirando: decide de quién es la caja del comprobante. */
  viewerId: string;
  /** Su sexo, para ofrecerle solo las categorías en las que puede jugar. */
  viewerSex: 'MALE' | 'FEMALE' | null;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<string>(TODAS);
  const [query, setQuery] = useState('');

  const buscado = query.trim().toLowerCase();
  const encontrados = tournament.entries
    .filter((e) => filter === TODAS || categoryKey(e) === filter)
    .filter(
      (e) =>
        buscado === '' ||
        `${e.displayName} ${e.firstName}`.toLowerCase().includes(buscado),
    )
    .slice()
    .sort(
      (a, b) =>
        // LO QUE HAY QUE REVISAR, PRIMERO. Lo pidió Javier con 68 inscritos
        // delante: «filtra primero los que debo aceptar». Con páginas de
        // diez, una inscripción sin revisar que cae en la página 5 no se
        // revisa nunca — y mientras tanto esa persona no sabe si está adentro.
        //
        // SOLO PARA QUIEN ORGANIZA. Para el resto no hay nada que hacer con
        // ese orden, y ver la lista barajada según un estado que no pueden
        // tocar es ruido: ahí manda el orden del cuadro.
        (tournament.canManage ? porRevisar(a) - porRevisar(b) : 0) ||
        // Después, por categoría y por sembrado: es el orden en que la gente
        // aparece en el cuadro, y así las dos listas se leen igual.
        categoryKey(a).localeCompare(categoryKey(b)) ||
        a.seed - b.seed,
    );

  /*
   * DE A DIEZ, COMO EL RESTO DE LA APLICACIÓN.
   *
   * Javier con la lista de 67 inscritos delante: para llegar al último hacía
   * falta arrastrar once pantallas, y cada fila trae foto — sesenta y siete
   * descargas antes de poder leer la primera.
   *
   * `reiniciarCon` LLEVA EL FILTRO Y EL TEXTO. Filtrar cambia la lista
   * entera, y quedarse en la página 3 de OTRA lista es lo que hace pensar que
   * la búsqueda no encontró nada.
   */
  const faltanRevisar = tournament.entries.filter((e) => porRevisar(e) === 0).length;

  const pagina = usePaginado(encontrados, `${filter}|${buscado}`);
  const visibles = pagina.visibles;

  return (
    <section>
      <div className="section-head">
        <h2>{t('entries.title')}</h2>
        <span className="tag">{t('tournaments.entryCount', { n: tournament.entries.length })}</span>
        {/* CUÁNTAS ESPERAN UNA DECISIÓN, y solo para quien puede tomarla. Con
            sesenta y ocho inscritos y páginas de diez, el número es lo que
            dice si hay trabajo pendiente sin tener que bajar a mirar. No se
            dibuja en cero: una etiqueta que dice «0 por revisar» ocupa el
            lugar de una y no informa nada. */}
        {tournament.canManage && faltanRevisar > 0 && (
          <span className="tag tag-pending">{t('entries.toReview', { n: faltanRevisar })}</span>
        )}
      </div>

      {tournament.entries.length === 0 ? (
        <p className="help">{t('tournaments.noEntriesYet')}</p>
      ) : (
        <>
          <div className="row">
            {/* CON LAS CATEGORÍAS A LA VISTA Y NO EN UN DESPLEGABLE.
                Un torneo tiene tres o cuatro; el desplegable las escondía
                todas detrás de un toque, y la lista que abre el sistema no
                respeta el tema oscuro y tapa la mitad de la pantalla. Ver
                `ChoiceGroup`, que existe por exactamente esto. */}
            {tournament.categories.length > 1 && (
              <ChoiceGroup
                id="entries-category"
                legend={t('entries.filter')}
                value={filter}
                onChange={setFilter}
                options={[
                  { value: TODAS, label: t('entries.allCategories') },
                  ...tournament.categories.map((c: CommunityCategory) => ({
                    value: categoryKey(c),
                    // Por GRUPO y no por sexo: el singles B masculino y el
                    // dobles B masculino dirían los dos «B · Masculino», que
                    // son dos filtros con la misma etiqueta; y el mixto no es
                    // un sexo de persona, así que `sex.MIXED` no existe.
                    label: `${t(`categoryLevel.${c.level}`)} · ${t(`categoryGroup.${c.discipline}_${c.sex}`)}`,
                  })),
                ]}
              />
            )}

            <div className="field">
              <label htmlFor="entries-search">{t('entries.search')}</label>
              <input
                id="entries-search"
                type="search"
                autoComplete="off"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
          </div>

          {visibles.length === 0 ? (
            <p className="help">{t('entries.noneFound')}</p>
          ) : (
            <ul className="entry-list">
              {visibles.map((entry) => (
                <li key={entry.id}>
                  <Avatar url={assetUrl(entry.avatarPath)} name={entry.displayName} size={36} />
                  <div className="member-name">
                    <strong>{entry.displayName}</strong>
                    <span className="help">
                      {entry.firstName} · {categoryName(t, entry)}
                      {entry.isSecondCategory && ` · ${t('tournaments.second')}`}
                    </span>
                    {/* LAS DOS SEÑALES DEL TORNEO ABIERTO, y solo para quien
                        organiza: son para decidir, y quien no decide nada no
                        gana nada sabiéndolo.

                        EN ROJO SOLO LA QUE PIDE UNA DECISIÓN. Venir de otro
                        club es lo normal en un torneo abierto y va en gris;
                        anotarse fuera de su nivel es lo que Javier quiere
                        ver, y va en rojo. Si las dos gritaran, ninguna
                        gritaría. */}
                    {tournament.canManage && fueraDeNivel(entry) && (
                      <span className="tag tag-rejected">
                        {t('entries.outOfLevel', {
                          level: t(`categoryLevel.${entry.assignedLevel ?? 'C'}`),
                        })}
                      </span>
                    )}
                    {tournament.canManage && !entry.isGuest && entry.assignedLevel === null && (
                      <span className="tag">{t('entries.outsideClubEntry')}</span>
                    )}
                  </div>
                  <PaymentMarks entry={entry} />
                  <Proof entry={entry} />
                  {tournament.canManage && (
                    <ReviewBox
                      tournamentId={tournament.id}
                      entry={entry}
                      onChanged={onChanged}
                    />
                  )}
                  {tournament.canManage && entry.isGuest && (
                    <LinkGuest
                      tournamentId={tournament.id}
                      communityId={tournament.communityId}
                      entry={entry}
                      onChanged={onChanged}
                    />
                  )}
                  {/*
                    REEMPLAZAR: sale uno y entra otro, la llave sigue igual.

                    EL COMPLEMENTO EXACTO DE `LinkGuest`, y por eso la
                    condición es la contraria: a un invitado se le PONE una
                    cuenta, y a quien ya tiene una se la CAMBIA. Las dos nunca
                    aparecen juntas en la misma fila.
                  */}
                  {tournament.canManage && !entry.isGuest && (
                    <ReplacePlayer
                      tournamentId={tournament.id}
                      communityId={tournament.communityId}
                      entry={entry}
                      yaEnElCuadro={tournament.entries}
                      onChanged={onChanged}
                    />
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* CON NOMBRE: la ficha del torneo puede tener varios paginadores
              a la vez —uno por categoría en borrador— y sin nombre propio
              todos suenan igual en un lector de pantalla. */}
          <Paginador
            pagina={pagina.pagina}
            paginas={pagina.paginas}
            irA={pagina.irA}
            de={t('entries.title')}
          />
        </>
      )}

      {/* La caja del comprobante, solo para las inscripciones PROPIAS. */}
      {tournament.entries
        // `!== null` primero: un invitado no puede mandar su comprobante
        // porque no tiene cuenta con la que entrar, y comparar un nulo a
        // secas contra el id de quien mira es la clase de igualdad que un día
        // contesta que sí.
        .filter((e) => e.userId !== null && e.userId === viewerId)
        .map((entry) => (
          <ProofBox
            key={entry.id}
            tournament={tournament}
            entry={entry}
            onChanged={onChanged}
          />
        ))}

      <SelfEnterBox
        tournament={tournament}
        viewerId={viewerId}
        viewerSex={viewerSex}
        onChanged={onChanged}
      />
    </section>
  );
}

/**
 * EL CLUB DA POR BUENO EL PAGO, O DICE QUE NO.
 *
 * Javier lo pidió así: «me pueden pasar un comprobante falso, o un texto sin
 * sentido, debo poder controlar eso».
 *
 * VA EN LA MISMA FILA QUE EL COMPROBANTE, al lado de la foto y del
 * comentario. Es lo único que hace decidible la decisión: mandar a quien
 * organiza a otra pantalla a aprobar una lista de nombres, sin el comprobante
 * delante, es pedirle que apruebe a ciegas.
 *
 * EL MOTIVO SE PIDE AL RECHAZAR Y NO AL APROBAR. Un «no» sin motivo deja a la
 * persona sin saber si mandar otra foto, escribir un comentario o llamar por
 * teléfono; un «sí» no necesita explicación.
 *
 * SE PUEDE CAMBIAR DE OPINIÓN. Aprobado y rechazado siguen mostrando el otro
 * botón: quien se equivocó de fila tiene que poder arreglarlo sin sacar a
 * nadie del torneo.
 */
function ReviewBox({
  tournamentId,
  entry,
  onChanged,
}: {
  tournamentId: string;
  entry: TournamentEntry;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [rechazando, setRechazando] = useState(false);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function decidir(approve: boolean, motivo?: string): Promise<void> {
    setError(null);
    setSending(true);
    try {
      onChanged(
        await api.reviewEntry(tournamentId, entry.id, {
          approve,
          ...(motivo === undefined || motivo.trim() === '' ? {} : { note: motivo.trim() }),
        }),
      );
      setRechazando(false);
      setNote('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (rechazando) {
    return (
      <span className="entry-review">
        {error !== null && <Alert kind="error">{error}</Alert>}
        <label className="visually-hidden" htmlFor={`review-note-${entry.id}`}>
          {t('entries.rejectReason')}
        </label>
        <input
          id={`review-note-${entry.id}`}
          type="text"
          value={note}
          maxLength={ENTRY_REVIEW_NOTE_MAX}
          placeholder={t('entries.rejectReason')}
          onChange={(e) => setNote(e.target.value)}
        />
        <button
          type="button"
          className="link danger"
          disabled={sending}
          onClick={() => void decidir(false, note)}
        >
          {t('entries.reject')}
        </button>
        <button type="button" className="link" onClick={() => setRechazando(false)}>
          {t('common.cancel')}
        </button>
      </span>
    );
  }

  return (
    <span className="entry-review">
      {error !== null && <Alert kind="error">{error}</Alert>}
      {/* CON EL RÓTULO DELANTE. Justo arriba está el comentario que escribió
          quien se inscribió, entrecomillado igual: sin decir de quién es cada
          uno, las dos citas se leen como una discusión sin nombres. */}
      {entry.reviewNote !== null && (
        <p className="help entry-review-note">
          <strong>{t('entries.clubSaid')}</strong> <q>{entry.reviewNote}</q>
        </p>
      )}
      {entry.reviewState !== 'APPROVED' && (
        <button
          type="button"
          className="link"
          disabled={sending}
          // DE QUIÉN. Hay uno por inscripción y todos decían «Aprobar»: en
          // una lista de veinte, un lector de pantalla anuncia veinte
          // botones idénticos.
          aria-label={t('entries.approveOf', { name: nombreParaLeer(entry) })}
          onClick={() => void decidir(true)}
        >
          {t('entries.approve')}
        </button>
      )}
      {entry.reviewState !== 'REJECTED' && (
        <button
          type="button"
          className="link danger"
          disabled={sending}
          // Y acá pesa más que en «Aprobar»: rechazar le llega a esa persona
          // como un aviso, así que equivocarse de fila se nota afuera.
          aria-label={t('entries.rejectOf', { name: nombreParaLeer(entry) })}
          onClick={() => setRechazando(true)}
        >
          {t('entries.reject')}
        </button>
      )}
    </span>
  );
}

/**
 * PONERLE UNA CUENTA A UN INVITADO.
 *
 * El sábado esa persona jugó sin tener la aplicación; el martes se hizo la
 * cuenta. Vincular no reescribe nada de lo que pasó: los partidos y el cuadro
 * ya eran suyos. Lo que cambia es que desde ahí el sistema SABE que eran
 * suyos, así que su foto aparece en el cuadro y los puntos de este torneo
 * entran a su ranking del club.
 *
 * VA EN LA FILA DE ESA PERSONA y no en una pantalla aparte: quien vincula
 * está mirando la nómina y reconociendo nombres, y mandarlo a otro lado lo
 * obliga a acordarse de a cuál de los tres invitados le iba a poner cuenta.
 *
 * BUSCA EN EL CLUB, con el mismo buscador que usa el alta. Quien acaba de
 * hacerse la cuenta y pidió entrar al club aparece ahí, que es exactamente el
 * caso para el que existe esto.
 */
function LinkGuest({
  tournamentId,
  communityId,
  entry,
  onChanged,
}: {
  tournamentId: string;
  communityId: string;
  entry: TournamentEntry;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const busqueda = usePlayerSearch(communityId, query);

  async function vincular(userId: string): Promise<void> {
    setError(null);
    setSending(true);
    try {
      onChanged(await api.linkGuestEntry(tournamentId, entry.id, userId));
      setAbierto(false);
      setQuery('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (!abierto) {
    return (
      <button type="button" className="link entry-link-guest" onClick={() => setAbierto(true)}>
        {t('tournaments.guestLink')}
      </button>
    );
  }

  return (
    <div className="guest-box entry-link-box">
      <h3>{t('tournaments.guestLinkTitle', { name: entry.displayName })}</h3>
      <p className="help">{t('tournaments.guestLinkHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor={`link-${entry.id}`}>{t('members.searchLabel')}</label>
        <input
          id={`link-${entry.id}`}
          type="search"
          autoComplete="off"
          value={query}
          placeholder={t('members.searchPlaceholder')}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <ul className="member-list">
        {busqueda.results
          // SOLO LOS DEL MISMO SEXO QUE LA INSCRIPCIÓN. La inscripción guardó
          // un sexo el día que se creó, y ese es el cuadro donde jugó: meter
          // ahí a alguien del otro lo dejaría en el cuadro equivocado con su
          // propia foto encima. El servidor lo rechaza igual; acá se evita
          // ofrecer un botón que va a rebotar.
          .filter((p) => p.sex === entry.sex)
          .map((player) => (
            <li key={player.userId}>
              <Avatar url={assetUrl(player.avatarPath)} name={player.displayName} size={36} />
              <div className="member-name">
                <strong>{player.displayName}</strong>
                <span className="help">{player.fullName}</span>
              </div>
              <button type="button" disabled={sending} onClick={() => void vincular(player.userId)}>
                {t('tournaments.guestLinkDo')}
              </button>
            </li>
          ))}
      </ul>

      <div className="row-actions">
        <button type="button" className="secondary" onClick={() => setAbierto(false)}>
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}

/**
 * SALE UNO Y ENTRA OTRO, en la misma plaza del cuadro.
 *
 * Javier: «el administrador del club y los organizadores del torneo pueden
 * reemplazar jugadores de los torneos sale Javier y entra Pedro y Pedro sigue
 * las llaves del torneo».
 *
 * SE PARECE A `LinkGuest` Y NO ES LO MISMO. Vincular le pone cuenta a un
 * invitado que no tenía: no hay nada que perder. Reemplazar le cambia la
 * persona a una plaza que puede tener partidos jugados, y con ellos se van el
 * historial y los puntos. Por eso esto PIDE CONFIRMACIÓN y aquello no: son
 * dos gestos de riesgo distinto y la pantalla tiene que notarlo.
 *
 * EN DOS PASOS —elegir y confirmar— y no en uno con un `confirm()`: el aviso
 * tiene que decir QUIÉN sale y QUIÉN entra, y eso no cabe en un diálogo del
 * navegador que además no se puede traducir.
 */
function ReplacePlayer({
  tournamentId,
  communityId,
  entry,
  yaEnElCuadro,
  onChanged,
}: {
  tournamentId: string;
  communityId: string;
  entry: TournamentEntry;
  /** Todas las inscripciones del torneo, para no ofrecer a quien ya está. */
  yaEnElCuadro: readonly TournamentEntry[];
  onChanged: (tournament: Tournament) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [abierto, setAbierto] = useState(false);
  const [query, setQuery] = useState('');
  const [elegido, setElegido] = useState<{ userId: string; displayName: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const busqueda = usePlayerSearch(communityId, query);

  function cerrar(): void {
    setAbierto(false);
    setQuery('');
    setElegido(null);
    setError(null);
  }

  async function reemplazar(userId: string): Promise<void> {
    setError(null);
    setSending(true);
    try {
      onChanged(await api.replaceEntryPlayer(tournamentId, entry.id, userId));
      cerrar();
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  if (!abierto) {
    return (
      <button type="button" className="link entry-replace" onClick={() => setAbierto(true)}>
        {t('tournaments.replacePlayer')}
      </button>
    );
  }

  /*
   * QUIÉNES NO SE OFRECEN.
   *
   * Los de otro sexo que el cuadro —salvo en mixto, donde juegan los dos— y
   * quienes YA están en esta misma categoría: meterlos ahí los dejaría
   * jugando contra sí mismos. El servidor lo rechaza igual; acá se evita
   * ofrecer un botón que va a rebotar.
   *
   * QUIEN YA OCUPA ESTA PLAZA TAMPOCO, y es un caso aparte del anterior:
   * reemplazar a alguien por sí mismo no es un cambio, y el servidor lo
   * contesta con su propio mensaje.
   */
  const enEsteCuadro = new Set(
    yaEnElCuadro
      .filter(
        (e) =>
          e.level === entry.level && e.sex === entry.sex && e.discipline === entry.discipline,
      )
      .map((e) => e.userId)
      .filter((id): id is string => id !== null),
  );

  const candidatos = busqueda.results.filter(
    (p) => (entry.sex === 'MIXED' || p.sex === entry.sex) && !enEsteCuadro.has(p.userId),
  );

  return (
    <div className="guest-box entry-replace-box">
      <h3>{t('tournaments.replaceTitle', { name: entry.displayName })}</h3>

      {elegido === null ? (
        <>
          <p className="help">{t('tournaments.replaceHelp')}</p>

          {error !== null && <Alert kind="error">{error}</Alert>}

          <div className="field">
            <label htmlFor={`replace-${entry.id}`}>{t('members.searchLabel')}</label>
            <input
              id={`replace-${entry.id}`}
              type="search"
              autoComplete="off"
              value={query}
              placeholder={t('members.searchPlaceholder')}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          <ul className="member-list">
            {candidatos.map((player) => (
              <li key={player.userId}>
                <Avatar url={assetUrl(player.avatarPath)} name={player.displayName} size={36} />
                <div className="member-name">
                  <strong>{player.displayName}</strong>
                  <span className="help">{player.fullName}</span>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    setElegido({ userId: player.userId, displayName: player.displayName })
                  }
                >
                  {t('tournaments.replaceChoose')}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <>
          {/*
            EL AVISO DICE LOS DOS NOMBRES Y QUÉ SE LLEVA QUIEN ENTRA.
            Que los puntos cambien de dueño es la consecuencia menos evidente
            de este botón, así que se dice acá y no en la ayuda de arriba, que
            nadie lee dos veces.
          */}
          <Alert kind="info">
            {t('tournaments.replaceConfirm', {
              out: entry.displayName,
              in: elegido.displayName,
            })}
          </Alert>

          {error !== null && <Alert kind="error">{error}</Alert>}

          <div className="row-actions">
            <button
              type="button"
              disabled={sending}
              onClick={() => void reemplazar(elegido.userId)}
            >
              {sending ? t('common.sending') : t('tournaments.replaceYes')}
            </button>
            <button type="button" className="secondary" onClick={() => setElegido(null)}>
              {t('common.back')}
            </button>
          </div>
        </>
      )}

      {elegido === null && (
        <div className="row-actions">
          <button type="button" className="secondary" onClick={cerrar}>
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * EL COMPROBANTE, ABIERTO.
 *
 * El ícono dice QUE hay comprobante; esto es el comprobante. Quien cobra
 * necesita las dos cosas: el ícono para recorrer la lista de un vistazo y el
 * contenido para darlo por bueno —mirar el monto del SINPE, leer «lo arreglé
 * con Marco»—. Con solo el ícono, verificar un pago obliga a salir de la
 * pantalla y preguntar por WhatsApp.
 *
 * El servidor solo se lo manda a quien organiza y a la persona misma (ver
 * `toEntry`), así que acá no hay nada que decidir: si llegó, se muestra.
 *
 * La imagen se abre en pestaña nueva y NO se agranda acá adentro: en la
 * lista lo que se necesita es la miniatura para reconocerla, y quien tiene
 * que leer el monto la abre entera.
 */
function Proof({ entry }: { entry: TournamentEntry }): JSX.Element | null {
  const { t } = useTranslation();
  const imagen = assetUrl(entry.proofImagePath);
  const comentario = entry.proofComment;
  if (imagen === null && comentario === null) return null;

  return (
    <span className="entry-proof">
      {imagen !== null && (
        <a href={imagen} target="_blank" rel="noopener noreferrer" title={t('entries.openProof')}>
          <img className="proof-thumb" src={imagen} alt={t('entries.paidImage')} />
        </a>
      )}
      {comentario !== null && <q className="help">{comentario}</q>}
    </span>
  );
}

/**
 * EL ÍCONO DE CÓMO PAGÓ.
 *
 * Uno solo por fila: el medio, cuando se sabe, y el reloj cuando todavía no
 * llegó nada. Antes salía del estado DERIVADO —foto, comentario o nada— y
 * ahora del medio DECLARADO, que dice más: la transferencia y el SINPE
 * llegan las dos como foto, y el patrocinio no llega como nada.
 *
 * Sin medio declarado se cae al derivado. No es lo mismo que no tener dato:
 * las inscripciones de antes de que esto existiera tienen foto o comentario y
 * ninguna declaración, y mostrarles un reloj diría que no pagaron.
 */
function MetodoIcon({ entry }: { entry: TournamentEntry }): JSX.Element | null {
  const { t } = useTranslation();
  const nombre = (m: string): string => t(`paymentMethod.${m}`);

  switch (entry.paymentMethod) {
    case 'SINPE':
      return <PhotoIcon title={nombre('SINPE')} />;
    case 'TRANSFER':
      return <BankIcon title={nombre('TRANSFER')} />;
    case 'COMMENT':
      return <CommentIcon title={nombre('COMMENT')} />;
    case 'SPONSOR':
      return <StarIcon title={nombre('SPONSOR')} />;
    case 'ORGANIZER':
      return <HandIcon title={nombre('ORGANIZER')} />;
    default:
      // Sin declarar: lo que se sepa por el comprobante, y si no hay, el reloj.
      if (entry.paymentState === 'IMAGE') return <PhotoIcon title={t('entries.paidImage')} />;
      if (entry.paymentState === 'COMMENT') return <CommentIcon title={t('entries.paidComment')} />;
      return (
        <span className="mark-warn">
          <ClockIcon title={t('entries.pending')} />
        </span>
      );
  }
}

/**
 * LO QUE DECIDIÓ EL CLUB. Un visto, una cruz, o nada.
 *
 * NADA CUANDO ESTÁ SIN REVISAR, y es a propósito: casi todas las filas están
 * así, y un tercer ícono repetido en toda la lista no distingue nada. Lo que
 * hay que ver de un golpe es cuáles ya se miraron.
 */
function RevisionMark({ entry }: { entry: TournamentEntry }): JSX.Element | null {
  const { t } = useTranslation();
  if (entry.reviewState === 'APPROVED') {
    return (
      <span className="mark-ok">
        <CheckIcon title={t('entries.approved')} />
      </span>
    );
  }
  if (entry.reviewState === 'REJECTED') {
    return (
      <span className="mark-bad">
        <CrossIcon title={t('entries.rejected')} />
      </span>
    );
  }
  return null;
}

/** Los íconos de una fila. Ver el comentario de arriba. */
export function PaymentMarks({ entry }: { entry: TournamentEntry }): JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="entry-marks">
      <MetodoIcon entry={entry} />
      <RevisionMark entry={entry} />
      {/* La mano de «lo metió el club» solo cuando el medio NO lo dice ya: con
          `ORGANIZER` el ícono ya es la mano, y dos manos seguidas no agregan
          nada. */}
      {entry.addedByOrganizer && entry.paymentMethod !== 'ORGANIZER' && (
        <HandIcon title={t('entries.addedByOrganizer')} />
      )}
      {/*
        LA MARCA DE INVITADO ES UNA PALABRA Y NO UN ÍCONO.

        Los demás íconos de esta fila dicen cómo llegó el pago —tres estados
        de la misma cosa, que se aprenden mirándolos juntos—. «Invitado» dice
        algo de otra naturaleza: que esa persona no tiene cuenta, y por lo
        tanto no le van a llegar los avisos ni le suman puntos. Un cuarto
        ícono ahí se leería como un cuarto estado de pago.
      */}
      {entry.isGuest && <span className="tag tag-guest">{t('tournaments.guestTag')}</span>}
    </span>
  );
}

/**
 * Mandar el comprobante de la inscripción propia.
 *
 * SOLO APARECE SI FALTA. Con el comprobante ya mandado, una caja para
 * volver a mandarlo invita a mandarlo dos veces y a preguntarse si el
 * primero llegó.
 */
function ProofBox({
  tournament,
  entry,
  onChanged,
}: {
  /* EL TORNEO ENTERO Y NO SOLO SU ID: de acá salen el monto y las cuentas, y
     pedirlos aparte sería otra consulta para dos renglones de texto justo
     cuando alguien está apurado por anotarse. */
  tournament: Tournament;
  entry: TournamentEntry;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element | null {
  const tournamentId = tournament.id;

  /*
   * CUÁNTO HAY QUE PAGAR POR ESTA INSCRIPCIÓN.
   *
   * La segunda categoría de la misma persona puede valer distinto, y
   * `secondCategoryFeeColones` en nulo significa «lo mismo que la primera» —
   * no «gratis». Confundir esas dos cosas le cobraría cero a media lista.
   */
  const monto =
    entry.isSecondCategory && tournament.secondCategoryFeeColones !== null
      ? tournament.secondCategoryFeeColones
      : tournament.feeColones;
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (entry.paymentState !== 'PENDING') return null;

  async function enviar(): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(
        await api.setEntryProof(tournamentId, entry.id, {
          ...(comment.trim() === '' ? {} : { comment: comment.trim() }),
          ...(image === null ? {} : { image }),
        }),
      );
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="proof-box">
      <h3>{t('entries.proofTitle')}</h3>
      {/* El plazo se dice EN LA CAJA y no en un cartel aparte: es la razón de
          que la caja exista, y a un cartel arriba de todo nadie lo lee. */}
      <p className="help">
        {entry.expiresAt === null
          ? t('entries.proofHelp')
          : t('entries.proofDeadline', { time: hora(entry.expiresAt) })}
      </p>

      {/*
        CUÁNTO Y A DÓNDE, ANTES DE PEDIR LA FOTO.
        La caja pedía el comprobante de un pago sin decir cuánto había que
        pagar ni a qué cuenta: el número andaba por el grupo de WhatsApp del
        club, y quien se anotaba de noche no tenía dónde buscarlo.
      */}
      <p className="proof-amount">
        {t('entries.amountToPay', { amount: formatColones(monto) })}
        {entry.isSecondCategory && (
          <span className="help"> {t('entries.secondCategoryFee')}</span>
        )}
      </p>

      {/* SIN CUENTAS NO SE DIBUJA EL TÍTULO: un encabezado seguido de nada se
          lee como que algo no cargó, y hay clubes que cobran en efectivo. */}
      {tournament.accounts.length > 0 && (
        <div className="proof-accounts">
          <h4>{t('entries.accounts')}</h4>
          <ul>
            {tournament.accounts.map((cuenta) => (
              <li key={cuenta.id}>
                {/* EL NÚMERO COMO TEXTO SELECCIONABLE, en su propio renglón:
                    lo que la gente hace con él es copiarlo y pegarlo en la
                    app del banco, y un número metido en medio de una frase se
                    selecciona mal con el dedo. */}
                <span className="proof-account-number">{cuenta.number}</span>
                <span className="help">{cuenta.description}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="field">
        <label htmlFor={`proof-comment-${entry.id}`}>{t('entries.proofComment')}</label>
        <input
          id={`proof-comment-${entry.id}`}
          type="text"
          maxLength={500}
          value={comment}
          disabled={sending}
          placeholder={t('entries.proofCommentPlaceholder')}
          onChange={(e) => setComment(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor={`proof-image-${entry.id}`}>{t('entries.proofImage')}</label>
        <input
          id={`proof-image-${entry.id}`}
          type="file"
          accept="image/*"
          disabled={sending}
          onChange={(e) => setImage(e.target.files?.[0] ?? null)}
        />
      </div>

      <button
        type="button"
        disabled={sending || (comment.trim() === '' && image === null)}
        onClick={() => void enviar()}
      >
        {t('entries.proofSend')}
      </button>
    </div>
  );
}

/** La hora local, sin fecha: el plazo siempre es hoy. */
function hora(iso: string): string {
  const cuando = new Date(iso);
  return Number.isNaN(cuando.getTime())
    ? ''
    : cuando.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Anotarse uno mismo.
 *
 * NO NECESITA PERMISO DE NADIE. Un torneo al que hay que pedirle permiso a
 * alguien para entrar no es un torneo abierto: el club decide las categorías
 * y el precio, y quien quiere jugar se anota. Lo que viene después —el
 * comprobante y su hora— es lo que evita que el cupo quede tomado sin pagar.
 *
 * SOLO LAS CATEGORÍAS DE SU SEXO. Ofrecer las otras sería ofrecer un error:
 * el servidor las rechaza, y un formulario que ofrece opciones que no
 * funcionan enseña a desconfiar del formulario.
 */
function SelfEnterBox({
  tournament,
  viewerId,
  viewerSex,
  onChanged,
}: {
  tournament: Tournament;
  viewerId: string;
  viewerSex: 'MALE' | 'FEMALE' | null;
  onChanged: (tournament: Tournament) => void;
}): JSX.Element | null {
  const { t } = useTranslation();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** El cuadro de dobles que se está por tomar, mientras se elige la pareja. */
  const [conPareja, setConPareja] = useState<CommunityCategory | null>(null);
  const [queryPareja, setQueryPareja] = useState('');
  /*
   * LA PAREJA TIENE QUE PODER JUGAR ESE NIVEL, igual que quien la anota: el
   * servidor revisa a los dos. Buscar sin el filtro ofrecería gente que el
   * botón no puede inscribir.
   *
   * Los `hooks` no pueden ir después de un `return`, así que esto se arma
   * siempre y la clave es `null` mientras no haya cuadro elegido.
   */
  const buscandoPareja = usePlayerSearch(
    tournament.communityId,
    queryPareja,
    /*
     * LA LLAVE LLEVA EL SEXO DE LA PAREJA, NO EL DEL CUADRO.
     *
     * `categoryKey(conPareja)` sería lo obvio y está mal: en el mixto el sexo
     * del cuadro es `MIXED`, que no es el sexo de ninguna persona, y el filtro
     * devolvería cero. `partnerSexFor` contesta a quién le toca —al hombre una
     * mujer, a la mujer un hombre— y es la misma regla que después aplica
     * `checkPair` en el servidor.
     */
    conPareja === null || viewerSex === null
      ? null
      : claveDeBusqueda(conPareja.level, partnerSexFor(conPareja, viewerSex)),
  );

  if (viewerSex === null) {
    return <p className="help">{t('tournaments.needSexInProfile')}</p>;
  }

  const mias = tournament.entries.filter((e) => e.userId === viewerId);

  /**
   * EN QUÉ ME PUEDO ANOTAR: la regla completa, del contrato.
   *
   * Se importa en vez de reescribirse porque es la MISMA que aplica el
   * servidor en `addEntry` y la misma que usa la tarjeta del listado para
   * decir «podés anotarte». Con tres copias, el día que cambie va a cambiar
   * en una sola — y la que se vería en pantalla sería la equivocada.
   *
   * ACÁ HABÍA UN DEFECTO. Esto miraba `tournament.status !== 'DRAFT'` y se
   * escondía entero: un torneo «en juego» porque el A masculino ya arrancó
   * dejaba de ofrecer el B femenino, que el servidor SÍ aceptaba. La regla
   * de verdad es por CUADRO, no por torneo.
   */
  // `myLevel` es el nivel de QUIEN MIRA en este club, que es justo lo que
  // hace falta acá: esta caja es para anotarse uno mismo.
  const posibles = enterableCategories(tournament, {
    id: viewerId,
    sex: viewerSex,
    level: tournament.myLevel,
  });
  if (posibles.length === 0) return null;

  async function anotarse(categoria: CommunityCategory, partnerUserId: string | null): Promise<void> {
    setSending(true);
    setError(null);
    try {
      onChanged(
        await api.addTournamentEntry(tournament.id, viewerId, categoria, partnerUserId),
      );
      setConPareja(null);
      setQueryPareja('');
    } catch (caught) {
      setError(errorMessage(t, caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="self-enter">
      <h3>{mias.length === 0 ? t('entries.enterTitle') : t('entries.enterSecondTitle')}</h3>
      <p className="help">{t('entries.enterHelp')}</p>

      {error !== null && <Alert kind="error">{error}</Alert>}

      <div className="button-row">
        {posibles.map((c) => (
          <button
            key={categoryKey(c)}
            type="button"
            disabled={sending}
            /*
             * EN DOBLES EL BOTÓN NO INSCRIBE: PREGUNTA CON QUIÉN.
             *
             * Mandarlo sin pareja sería un botón que siempre contesta que no
             * —el servidor rechaza un dobles sin pareja, y hace bien—, así que
             * el mismo botón abre el paso que falta en vez de fallar.
             */
            onClick={() => {
              if (c.discipline === 'DOUBLES') {
                setError(null);
                setQueryPareja('');
                setConPareja(c);
                return;
              }
              void anotarse(c, null);
            }}
          >
            {t(`categoryLevel.${c.level}`)} · {t(`categoryGroup.${c.discipline}_${c.sex}`)}
          </button>
        ))}
      </div>

      {conPareja !== null && (
        <div className="partner-pick">
          <h4>
            {t('entries.partnerTitle', {
              category: `${t(`categoryLevel.${conPareja.level}`)} · ${t(`categoryGroup.${conPareja.discipline}_${conPareja.sex}`)}`,
            })}
          </h4>
          {/*
            * EL AVISO DEL MIXTO VA ANTES DE BUSCAR, no después de elegir.
            * Dicho al final, la persona ya escribió un nombre y lo que recibe
            * es un error; dicho acá, es una instrucción.
            */}
          <p className="help">
            {conPareja.sex === 'MIXED' ? t('entries.partnerMixedHelp') : t('entries.partnerHelp')}
          </p>
          <input
            type="search"
            value={queryPareja}
            onChange={(e) => setQueryPareja(e.target.value)}
            placeholder={t('entries.partnerSearch')}
            aria-label={t('entries.partnerSearch')}
          />
          {buscandoPareja.tooShort && (
            <p className="help">{t('members.keepTyping', { min: MIN_SEARCH_LENGTH })}</p>
          )}
          <ul className="plain-list">
            {buscandoPareja.results
              /*
               * NI YO MISMO, NI QUIEN YA ESTÁ EN ESE CUADRO, NI QUIEN NO
               * FORMA PAREJA CONMIGO ACÁ. Ofrecerlos es ofrecer un botón que
               * el servidor va a rechazar.
               *
               * Lo del sexo lo pregunta `checkPair` —la MISMA función que
               * decide en el servidor, no una copia— y no es desconfianza de
               * más: el filtro de sexo viaja en la consulta, y el día que se
               * pierda por el camino la lista pasaría a ofrecer hombres para
               * el mixto y cada toque terminaría en un error.
               */
              .filter(
                (p) =>
                  p.userId !== viewerId &&
                  checkPair(conPareja, { id: viewerId, sex: viewerSex }, p) === null &&
                  !tournament.entries.some(
                    (e) => categoryKey(e) === categoryKey(conPareja) && playsIn(e, p.userId),
                  ),
              )
              .map((p) => (
                <li key={p.userId}>
                  <button
                    type="button"
                    disabled={sending}
                    onClick={() => void anotarse(conPareja, p.userId)}
                  >
                    {p.displayName}
                  </button>
                </li>
              ))}
          </ul>
          {/* `link` y no un botón entero: al lado del que sí inscribe, un
              «Cancelar» del mismo tamaño y del mismo color compite con él. Es
              la misma clase que usa el «Cancelar» de rechazar un pago, dos
              cajas más arriba. */}
          <button type="button" className="link" onClick={() => setConPareja(null)}>
            {t('common.cancel')}
          </button>
        </div>
      )}
    </div>
  );
}
